import { tap, catchError, map, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { DataService } from './../../../core/service/base/data.service';
import { Contact } from './../../model/contact';
import { Payer } from '../../model/payer';
import { Contract } from '../../model/contract';
import { Creditor } from '../../model/creditor';
import { RentApplication } from '../../model/rent-application';
import { RentApplicationContact } from '../../model/rent-application-contact';
import { ContactBalanceTransaction } from '../../model/contact-balance-transaction';
import { Money } from '../../../core/model/money';
import { IdType } from '../../model/id-type';
import { IB2CContactToggleResult } from '../../../invoice/service/data/b2c-subscription-contact.service';
import { IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { ActivatedRoute } from '@angular/router';
import { ContactPaymentInformationPerCompany } from '../../model/contact-payment-information-per-company';
import { ContactB2CSubscriptionInformation } from '../../model/contact-b2cSubscription-information';
import { ApiVersions } from '../../../core/model/const/api-versions';

type ContactAutocompleteOptions = string | { displayFormat?: string };

@Injectable()
export class ContactService extends DataService<Contact> implements IAutoCompleteService {
  private readonly _defaultFilterFields: string[] = ['number', 'name', 'email', 'contactIdentification'];
  private readonly _contractTenantFilterDisplayFormat = 'contractTenantFilter';

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private messageService: MessageService,
    progressBar: ProgressBarService,
    private _route: ActivatedRoute
    ) {
    super(client, config, messageService, Contact, progressBar, 'contact');
  }

  protected createModel(data: any): Contact {
    const model = super.createModel(data);
    if (data.leaseAgreements instanceof Array) {
      model.leaseAgreements = data.leaseAgreements.map(item => Object.assign(new Contract(item.category), item));
    }

    if (data.rentApplications instanceof Array) {
      model.rentApplications = data.rentApplications.map(item => Object.assign(new RentApplication(), item));
    }

    if (data.contactBalanceTransactions instanceof Array) {
      model.contactBalanceTransactions = data.contactBalanceTransactions.map(item =>
        Object.assign(new ContactBalanceTransaction(item), item)
      );
    }

    if (data.contactPaymentInformationPerCompanies instanceof Array) {
      model.contactPaymentInformationPerCompanies = data.contactPaymentInformationPerCompanies.map(item =>
        Object.assign(new ContactPaymentInformationPerCompany(item), item)
      );
    }

    if (data.b2CSubscriptionContactInformations instanceof Array) {
      model.b2CSubscriptionContactInformations = data.b2CSubscriptionContactInformations.map(item =>
        Object.assign(new ContactB2CSubscriptionInformation(item), item)
      );
    }

    return model;
  }

  getFilterDefinition(): Observable<{
    number: string;
    name: string;
    email: string;
    phone: string;
    contactIdentification: string;
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          number: res.number ? res.number : '',
          name: res.name ? res.name : '',
          email: res.email ? res.email : '',
          phone: res.phone ? res.phone : '',
          contactIdentification: res.contactIdentification ? res.contactIdentification : '',
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  change(model: Contact, options: { notifyUser: boolean } = { notifyUser: true }): Observable<IB2CContactToggleResult[]> {
    const response: Observable<IB2CContactToggleResult[]> = super.change(model, options);
    return response.pipe(map(b2cChanges => b2cChanges?.map(change => Object.assign({}, change)) ?? []));
  }

  cleanupData(id: string): Observable<any> {
    return this.client.put(`${this.endpoint}/cleanup`, JSON.stringify(id)).pipe(
      tap(() => this.notification.showSuccess('SUCCESS.CLEANUP_TENANT_DATA')),
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      })
    );
  }

  getIdTypes(): Observable<IdType[]> {
    return this.client.get<any[]>(`${this.endpoint}/id-types`).pipe(map(val => val.map(item => new IdType(item.category, item.idType))));
  }

  getNameByCivicOrOrgNo(currentContactId: string, civicOrOrg: string): Observable<string> {
    if (currentContactId) {
      return this.client.get<string>(
        `${this.endpoint}/civicOrg?civicOrgNo=${civicOrOrg}&excludeId=${currentContactId ? currentContactId : 'null'}`
      );
    }
    return this.client.get<string>(`${this.endpoint}/civicOrgNew?civicOrgNo=${civicOrOrg}`);
  }

  createPaymentInformation(id: string): Observable<Contact> {
    return this.client.post<Contact>(`${this.endpoint}/create-payment-information`, JSON.stringify(id)).pipe(
      map(data => this.createModel(data)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  getGdpr(id: string): Observable<Blob> {
    return this.getPdf(`gdpr/${id}`);
  }

  getCreditors(): Observable<Creditor[]> {
    return this.client
      .get<Creditor[]>(`${this.endpoint}/creditors`)
      .pipe(map(items => items.map(item => Object.assign(new Creditor(), item))));
  }

  getCreditor(id: string): Observable<Creditor> {
    return this.client.get<Creditor>(`${this.endpoint}/creditor/${id}`).pipe(map(val => Object.assign(new Creditor(), val)));
  }

  getRentApplicationContacts(): Observable<RentApplicationContact[]> {
    return this.client
      .get<RentApplicationContact[]>(`${this.endpoint}/rent-application-contacts`)
      .pipe(map(items => items.map(item => Object.assign(new RentApplicationContact(), item))));
  }

  getRentApplicationContact(id: string): Observable<RentApplicationContact> {
    return this.client
      .get<RentApplicationContact>(`${this.endpoint}/rent-application-contact/${id}`)
      .pipe(map(val => Object.assign(new RentApplicationContact(), val)));
  }

  getPaymentInformation(): Observable<Payer[]> {
    return this.client
      .get<Payer[]>(`${this.endpoint}/payment-information`)
      .pipe(map(items => items.map(item => Object.assign(new Payer(), item))));
  }

  getPaymentInformationForId(id: string): Observable<Payer> {
    return this.client.get<Payer>(`${this.endpoint}/payment-information/${id}`).pipe(map(item => Object.assign(new Payer(), item)));
  }

  getContactBalance(contactId: string, currencyCode: string): Observable<Money> {
    return this.client.get<Money>(`${this.endpoint}/balance/${contactId}/${currencyCode}`);
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Kontakter.xlsx');
  }

  exportFilter(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Kontakter.xlsx');
  }

  getSelectionItem(id: string, state?: AutoCompleteSelectionState<Contact, ContactAutocompleteOptions>): Observable<AutoCompleteSelectionItem> {
    console.log('Try to get lease id', this._route.snapshot.paramMap.get('id'));
    return this.client.get<Contact>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(data => this.createAutoCompleteSelectionItem(data, state?.options)),
      catchError(e => {
        this.messageService.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string, options?: ContactAutocompleteOptions, page?: number): Observable<AutoCompleteSelectionItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 10,
    };
    if (page && page > 0) queryArgs['page'] = page;

    return this.client.post<Contact[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item, options))),
      catchError(e => {
        this.messageService.showError(e);
        return [];
      })
    );
  }

  emailTakenCheck(email: string, contactId?: string | null): Observable<boolean> {
    const params: Record<string, string> = { email };
    if (contactId) {
      params.contactId = contactId;
    }

    return this.client.post<boolean>(`${this.endpoint}/email-taken`, JSON.stringify(params));
  }

  private createAutoCompleteSelectionItem(
    contact: Contact,
    options?: ContactAutocompleteOptions
  ): AutoCompleteSelectionItem {
    if (!contact) {
      return new AutoCompleteSelectionItem('', null, null);
    }

    if (this.isContractTenantFilterOptions(options)) {
      const maskedIdentification = this.maskContactIdentification(contact.contactIdentification);
      const name = `${contact.name}${maskedIdentification ? ' (' + maskedIdentification + ')' : ''}`.trim();
      return new AutoCompleteSelectionItem(name, contact.id, Object.assign(new Contact(), contact), contact.name?.trim());
    }

    let shortName = `${contact.number} ${contact.name}`.trim();
    let name =
      `${shortName}${contact.contactIdentification ? ' (' + contact.contactIdentification + ')' : ''} ${contact.phone ? ', ' + contact.phone : ''}`.trim();
    return new AutoCompleteSelectionItem(name, contact.id, Object.assign(new Contact(), contact), shortName);
  }

  private maskContactIdentification(contactIdentification?: string): string {
    if (!contactIdentification) {
      return '';
    }

    if (contactIdentification.length <= 4) {
      return 'xxxx';
    }

    return `${contactIdentification.slice(0, -4)}xxxx`;
  }

  private isContractTenantFilterOptions(options?: ContactAutocompleteOptions): options is { displayFormat?: string } {
    return typeof options === 'object' && options?.displayFormat === this._contractTenantFilterDisplayFormat;
  }
}
